import { useState } from "react";
import Loader from "react-loader-spinner";

import { ChartTile } from "../../components/ChartTile";
import { InformationTile } from "../../components/InformationTile";
import { PageDateSelector } from "../../components/PageDateSelector";
import { Table } from "../../components/Table";
import { useAppSelector } from "../../store/hooks";
import { getColumns } from "./columnData";
import {
  ChartContainer,
  Container,
  Content,
  Header,
  Spinner,
  Tab,
  TabContainer,
  TableContainer,
  TableContent,
  TableTitle,
  TileContainer,
  Title,
} from "./styles";
import { TableTypes } from "../../components/Table/Table.types";
import { UserPermissions } from "../../enums";
import { DatePermissionParams } from "../../services/api/types/Dashboards";
import { useDashboardData } from "./useDashboardData";

export const Dashboard = () => {
  const token = useAppSelector(state => state.auth.token);
  const user = useAppSelector(state => state.auth.user);

  const isExec = user.permissions.includes(UserPermissions.DashboardView);
  const isTeamLead = user.isTeamLead ?? false;

  const [dateRange, setDateRange] = useState<DatePermissionParams>({
    executive: isExec,
    is30Day: true,
    teamLeadId: isTeamLead ? user.id : "",
  });

  const [selectedTab, setSelectedTab] = useState<string>(
    isExec || !isTeamLead ? "Executive Overview" : "My Team"
  );

  const {
    checkIns,
    isLoadingCheckIns,
    overtimeWeeks,
    isLoadingOvertimeWeeks,
    timeOff,
    isLoadingTimeOff,
    leaderScore,
    isLoadingLeaderScore,
    averageTicket,
    isLoadingAverageTicket,
    averageComp,
    isLoadingAverageComp,
    teams,
    isLoadingTeams,
  } = useDashboardData({
    dateRange,
  });

  const handleTabSelect = (e: any) => {
    if (e.currentTarget.value === selectedTab) return;

    const newDateRange = {
      is30Day: dateRange.is30Day,
      is60Day: dateRange.is60Day,
      is90Day: dateRange.is90Day,
      executive: e.currentTarget.value === "Executive Overview",
      teamLeadId: isTeamLead ? user.id : "",
    };
    setSelectedTab(e.currentTarget.value);
    setDateRange(newDateRange);
  };

  const chartsLoading =
    isLoadingLeaderScore ||
    isLoadingCheckIns ||
    isLoadingOvertimeWeeks ||
    isLoadingTimeOff;

  const formatNumber = (number: number, type: string) => {
    //fix number to being a 0 if it is not a number
    if (typeof number === "string") {
      number = parseFloat(number);
    }
    if (typeof number !== "number") number = 0;
    if (type === "percent") {
      return Math.round(number * 100) + "%";
    } else if (type === "dollar") {
      return "$" + Math.round(number).toLocaleString();
    }
  };

  return (
    <Container>
      <Content>
        <Title>Teams Dashboard</Title>

        <Header isExec={isExec}>
          {(isExec || isTeamLead) && (
            <TabContainer>
              {isExec && (
                <Tab
                  value="Executive Overview"
                  onClick={(e: any) => handleTabSelect(e)}
                  isSelected={
                    selectedTab === "Executive Overview" ? true : false
                  }
                >
                  Executive Overview
                </Tab>
              )}
              {isTeamLead && (
                <Tab
                  value="My Team"
                  onClick={(e: any) => handleTabSelect(e)}
                  isSelected={selectedTab === "My Team" ? true : false}
                >
                  My Team
                </Tab>
              )}
            </TabContainer>
          )}
          <PageDateSelector
            alignRight={true}
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        </Header>

        <TileContainer>
          <InformationTile
            title={"Monthly Check-ins"}
            value={`${checkIns?.completed ?? 0}:
              ${formatNumber(checkIns?.percentage ?? 0, "percent")}`}
            isLoading={isLoadingCheckIns}
          />
          <InformationTile
            title={"Weeks in Overtime"}
            value={overtimeWeeks?.weeks ?? 0}
            isLoading={isLoadingOvertimeWeeks}
          />
          <InformationTile
            title={"Time Off"}
            value={timeOff?.hours ?? 0}
            hoverStateData={timeOff?.departments ?? []}
            hoverTitle={"name"}
            hoverValue={"hours"}
            isLoading={isLoadingTimeOff}
          />
        </TileContainer>

        <ChartContainer>
          {chartsLoading ? (
            <Spinner>
              <Loader type="Oval" color="#00BFFF" height={40} width={40} />
            </Spinner>
          ) : (
            <>
              <ChartTile
                title="Average Work Week (Hrs)"
                data={leaderScore?.teams}
                type="leaderScore"
                dataField="score"
              />
              <ChartTile
                title="Monthly Checkins"
                data={checkIns?.teams}
                type="checkins"
                dataField="completed"
              />
              <ChartTile
                title="Overtime Weeks"
                data={overtimeWeeks?.teams}
                type="overtimeWeeks"
                dataField="weeks"
              />
              <ChartTile
                title="Time Off/Missed (Hrs)"
                data={timeOff?.teams}
                type="timeOff"
                dataField="hours"
              />
            </>
          )}
        </ChartContainer>

        <TileContainer>
          {/* <InformationTile */}
          {/*   title={"Win Rate"} */}
          {/*   width={"206px"} */}
          {/*   value={formatNumber(dashboard.winRate.percentage, "percent")} */}
          {/*   isLoading={isLoading} */}
          {/* /> */}
          <InformationTile
            title={"Average Leader Score"}
            value={(leaderScore?.score ?? 0).toFixed(2)}
            isLoading={isLoadingLeaderScore}
          />
          <InformationTile
            title={"Average Ticket"}
            width={"206px"}
            value={formatNumber(averageTicket?.amount ?? 0, "dollar")}
            hoverStateData={averageTicket?.departments ?? []}
            hoverTitle={"name"}
            hoverValue={"amount"}
            hoverValueType={"dollar"}
            isLoading={isLoadingAverageTicket}
          />
          <InformationTile
            title={"Average Comp"}
            width={"206px"}
            value={formatNumber(averageComp?.amount ?? 0, "dollar")}
            hoverStateData={averageComp?.departments ?? []}
            hoverTitle={"name"}
            hoverValue={"amount"}
            hoverValueType={"dollar"}
            isLoading={isLoadingAverageComp}
          />
          {/* <InformationTile */}
          {/*   title={"Advantage Sales"} */}
          {/*   width={"206px"} */}
          {/*   value={formatNumber(dashboard.mamSales.amount, "percent")} */}
          {/*   hoverStateData={dashboard.mamSales.departments} */}
          {/*   hoverTitle={"name"} */}
          {/*   hoverValue={"amount"} */}
          {/*   hoverValueType={"percent"} */}
          {/*   isLoading={isLoading} */}
          {/* /> */}
          {/* <InformationTile */}
          {/*   title={"Referrals"} */}
          {/*   width={"206px"} */}
          {/*   value={formatNumber(dashboard.referrals.amount, "percent")} */}
          {/*   isLoading={isLoading} */}
          {/* /> */}
        </TileContainer>

        <TableContainer>
          <TableTitle>
            {selectedTab === "Executive Overview" ? "Leaders" : "My Team"}
          </TableTitle>
          <TableContent>
            <Table
              tableType={
                selectedTab === "Executive Overview"
                  ? TableTypes.Dashboards
                  : TableTypes.Members
              }
              tableWidth="1108px"
              isLoading={isLoadingTeams}
              defaultRows={10}
              columns={getColumns(token)}
              rowsPerPage={[10, 20, 35, 50]}
              data={teams ?? []}
            />
          </TableContent>
        </TableContainer>
      </Content>
    </Container>
  );
};
